import React, { useEffect, useState, useCallback } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";

const API_BASE_URL = "https://api.cupvisor.org/v2/vendor";

// optional field list to keep things tidy
const OPTIONAL_FIELDS = [
  "google_maps_link",
  "instagram",
  "facebook",
  "tiktok",
  "X",
  "telephone_number",
];

function AdminPage({ user, signOut }) {
  const [jwtToken, setJwtToken] = useState("");
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVendor, setNewVendor] = useState({
    name: "",
    address: "",
    description: "",
    owner: "",
    admin: "",
    manager: "",
    employee: "",
    active: true,
    // --- added optional fields (all optional) ---
    google_maps_link: "",
    instagram: "",
    facebook: "",
    tiktok: "",
    X: "",
    telephone_number: "",
  });
  const [updateVendor, setUpdateVendor] = useState(null);
  const [formMessage, setFormMessage] = useState("");

  // Get JWT token from Amplify session.
  useEffect(() => {
    async function loadSession() {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        setJwtToken(idToken);
      } catch (err) {
        console.error("Error loading session:", err);
      }
    }
    loadSession();
  }, [user]);

  // Fetch vendor data
  const fetchVendors = useCallback(async () => {
    if (!jwtToken) return;
    setLoading(true);
    try {
      const res = await fetch(API_BASE_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (res.status === 200) {
        const data = await res.json();
        // handle both formats: array or { vendors: [...] }
        const list = Array.isArray(data) ? data : Array.isArray(data?.vendors) ? data.vendors : [];
        setVendors(list);
        setError("");
      } else if (res.status === 403) {
        setError("Not authorized to view vendor data.");
      } else {
        setError("Error fetching vendor data.");
      }
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setError("Error fetching vendor data.");
    }
    setLoading(false);
  }, [jwtToken]);

  useEffect(() => {
    if (jwtToken) {
      fetchVendors();
    }
  }, [jwtToken, fetchVendors]);

  // Handler to add a vendor
  const handleAddVendor = async (e) => {
    e.preventDefault();
    try {
      // Build payload: include optional fields only if non-empty
      const payload = { ...newVendor };
      OPTIONAL_FIELDS.forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });

      const res = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 201) {
        await res.json(); // not used, but keeps symmetry
        setFormMessage("Vendor added successfully.");
        setNewVendor({
          name: "",
          address: "",
          description: "",
          owner: "",
          admin: "",
          manager: "",
          employee: "",
          active: true,
          google_maps_link: "",
          instagram: "",
          facebook: "",
          tiktok: "",
          X: "",
          telephone_number: "",
        });
        fetchVendors();
      } else {
        setFormMessage("Error adding vendor.");
      }
    } catch (err) {
      console.error("Error adding vendor:", err);
      setFormMessage("Error adding vendor.");
    }
  };

  // Handler to update a vendor
  const handleUpdateVendor = async (e) => {
    e.preventDefault();
    if (!updateVendor || !updateVendor.vendor_id) return;
    try {
      // Build payload: send the whole updateVendor; backend whitelists fields
      const payload = { ...updateVendor };
      // avoid sending PK/SK if present
      delete payload.PK;
      delete payload.SK;

      const res = await fetch(`${API_BASE_URL}?vendorId=${updateVendor.vendor_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 200) {
        setFormMessage("Vendor updated successfully.");
        setUpdateVendor(null);
        fetchVendors();
      } else {
        setFormMessage("Error updating vendor.");
      }
    } catch (err) {
      console.error("Error updating vendor:", err);
      setFormMessage("Error updating vendor.");
    }
  };

  // Handler to soft-delete a vendor
  const handleDeleteVendor = async (vendor_id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}?vendorId=${vendor_id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      if (res.status === 200) {
        fetchVendors();
      } else {
        setError("Error deleting vendor.");
      }
    } catch (err) {
      console.error("Error deleting vendor:", err);
      setError("Error deleting vendor.");
    }
  };

  const renderOptionalInputs = (state, setState, prefix = "") => (
    <>
      <div className="mb-2">
        <label>Google Maps Link</label>
        <input
          type="url"
          className="form-control"
          value={state.google_maps_link || ""}
          onChange={(e) => setState({ ...state, google_maps_link: e.target.value })}
          placeholder="https://maps.google.com/?q=..."
        />
      </div>
      <div className="mb-2">
        <label>Instagram</label>
        <input
          type="url"
          className="form-control"
          value={state.instagram || ""}
          onChange={(e) => setState({ ...state, instagram: e.target.value })}
          placeholder="https://instagram.com/yourhandle"
        />
      </div>
      <div className="mb-2">
        <label>Facebook</label>
        <input
          type="url"
          className="form-control"
          value={state.facebook || ""}
          onChange={(e) => setState({ ...state, facebook: e.target.value })}
          placeholder="https://facebook.com/yourpage"
        />
      </div>
      <div className="mb-2">
        <label>TikTok</label>
        <input
          type="url"
          className="form-control"
          value={state.tiktok || ""}
          onChange={(e) => setState({ ...state, tiktok: e.target.value })}
          placeholder="https://www.tiktok.com/@yourhandle"
        />
      </div>
      <div className="mb-2">
        <label>X (Twitter)</label>
        <input
          type="url"
          className="form-control"
          value={state.X || ""}
          onChange={(e) => setState({ ...state, X: e.target.value })}
          placeholder="https://x.com/yourhandle"
        />
      </div>
      <div className="mb-2">
        <label>Telephone Number</label>
        <input
          type="text"
          className="form-control"
          value={state.telephone_number || ""}
          onChange={(e) => setState({ ...state, telephone_number: e.target.value })}
          placeholder="+34 600 000 000"
        />
      </div>
    </>
  );

  // Render the admin page.
  return (
    <div className="container my-4">
      <h2>Administrator - Vendor Management</h2>
      {loading && <p>Loading vendor data...</p>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && (
        <>
          <button
            className="btn btn-primary mb-3"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? "Hide Add Form" : "Add Vendor"}
          </button>
          {showAddForm && (
            <form onSubmit={handleAddVendor} className="mb-4">
              <div className="mb-2">
                <label>Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={newVendor.name}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="mb-2">
                <label>Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={newVendor.address}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, address: e.target.value })
                  }
                />
              </div>
              <div className="mb-2">
                <label>Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={newVendor.description}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, description: e.target.value })
                  }
                />
              </div>

              {/* Optional socials / contact */}
              {renderOptionalInputs(newVendor, setNewVendor)}

              <div className="mb-2">
                <label>Owner (Email)</label>
                <input
                  type="email"
                  className="form-control"
                  value={newVendor.owner}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, owner: e.target.value })
                  }
                  required
                />
              </div>
              <div className="mb-2">
                <label>Admin (Comma-separated emails)</label>
                <input
                  type="text"
                  className="form-control"
                  value={newVendor.admin}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, admin: e.target.value })
                  }
                />
              </div>
              <div className="mb-2">
                <label>Manager (Comma-separated emails)</label>
                <input
                  type="text"
                  className="form-control"
                  value={newVendor.manager}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, manager: e.target.value })
                  }
                />
              </div>
              <div className="mb-2">
                <label>Employee (Comma-separated emails)</label>
                <input
                  type="text"
                  className="form-control"
                  value={newVendor.employee}
                  onChange={(e) =>
                    setNewVendor({ ...newVendor, employee: e.target.value })
                  }
                />
              </div>
              <button type="submit" className="btn btn-success mt-2">
                Add Vendor
              </button>
              {formMessage && (
                <div className="mt-2 alert alert-info">{formMessage}</div>
              )}
            </form>
          )}

          <h3>Vendor List</h3>
          {vendors.length > 0 ? (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Owner</th>
                  <th>Admin</th>
                  <th>Manager</th>
                  <th>Employee</th>
                  <th>Contacts / Socials</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((vendor) => (
                  <tr key={vendor.vendor_id}>
                    <td>{vendor.name}</td>
                    <td>{vendor.owner}</td>
                    <td>{vendor.admin}</td>
                    <td>{vendor.manager}</td>
                    <td>{vendor.employee}</td>
                    <td style={{ maxWidth: 280 }}>
                      {/* compact view of socials (may be 'n/a') */}
                      <div style={{ fontSize: "0.9em" }}>
                        <div>📍 {vendor.google_maps_link || "n/a"}</div>
                        <div>📸 {vendor.instagram || "n/a"}</div>
                        <div>📘 {vendor.facebook || "n/a"}</div>
                        <div>🎵 {vendor.tiktok || "n/a"}</div>
                        <div>𝕏 {vendor.X || "n/a"}</div>
                        <div>☎️ {vendor.telephone_number || "n/a"}</div>
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-warning btn-sm me-2"
                        onClick={() => setUpdateVendor(vendor)}
                      >
                        Update
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteVendor(vendor.vendor_id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No vendors available.</p>
          )}

          {updateVendor && (
            <div className="mt-4">
              <h4>Update Vendor</h4>
              <form onSubmit={handleUpdateVendor}>
                <div className="mb-2">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={updateVendor.name || ""}
                    onChange={(e) =>
                      setUpdateVendor({ ...updateVendor, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="mb-2">
                  <label>Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={updateVendor.address || ""}
                    onChange={(e) =>
                      setUpdateVendor({ ...updateVendor, address: e.target.value })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label>Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={updateVendor.description || ""}
                    onChange={(e) =>
                      setUpdateVendor({
                        ...updateVendor,
                        description: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Optional socials / contact */}
                {renderOptionalInputs(updateVendor, setUpdateVendor)}

                <div className="mb-2">
                  <label>Owner (Email)</label>
                  <input
                    type="email"
                    className="form-control"
                    value={updateVendor.owner || ""}
                    onChange={(e) =>
                      setUpdateVendor({ ...updateVendor, owner: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="mb-2">
                  <label>Admin (Comma-separated emails)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={updateVendor.admin || ""}
                    onChange={(e) =>
                      setUpdateVendor({ ...updateVendor, admin: e.target.value })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label>Manager (Comma-separated emails)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={updateVendor.manager || ""}
                    onChange={(e) =>
                      setUpdateVendor({ ...updateVendor, manager: e.target.value })
                    }
                  />
                </div>
                <div className="mb-2">
                  <label>Employee (Comma-separated emails)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={updateVendor.employee || ""}
                    onChange={(e) =>
                      setUpdateVendor({ ...updateVendor, employee: e.target.value })
                    }
                  />
                </div>
                <button type="submit" className="btn btn-primary mt-2">
                  Update Vendor
                </button>
                <button
                  type="button"
                  className="btn btn-secondary mt-2 ms-2"
                  onClick={() => setUpdateVendor(null)}
                >
                  Cancel
                </button>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default AdminPage;